import React from 'react';
import Subnav from './subnav/Subnav';
import GeneralResultsPage from './pages/GeneralResultsPage';
import BlogSections from './BlogSections';
import ShareYourStory from './ShareYourStory';
import BlogHomePage from './pages/BlogHomePage';
import Section from './models/Section';
import ArticlePage from './pages/ArticlePage';
import ArticleLinkList from './models/ArticleLinkList';
import Article from './models/Article';
import AuthorPage from './pages/AuthorPage';
import Author from './models/Author';
import articleTypes from './models/ArticleTypes';
import Taxonomy from './Taxonomy';
import WordpressConverter from './utils/WordpressConverter';
import Situation from './models/Situation';
import ArticleType from './models/ArticleType';
import Head from '@/components/Head';
import WordpressYoastMeta from '@gik/core/models/wordpress/WordpressYoastMeta';
import getPreviewMode from '@gik/core/utils/PreviewMode';
import { deleteDotNetLoadingSpinner } from '@gik/core/utils/DotNetUtils';

interface IBlogProps {
  section: string;
  taxonomySlug: string;
  pageSlug: string;
  tags?: Section[];
  situations?: Section[];
  articleLinkLists: ArticleLinkList[];
  article?: Article;
  author?: Author;
  situation?: Situation;
  articleType?: ArticleType;
  renderHeadEnabled?: boolean;
  getGeneralResultsPageName(): string;
}

export const blogEndpoint = 'articles';

export default function Blog(props: IBlogProps): React.ReactElement {
  function _renderHead() {
    const { article, author, articleType, situation } = props;
    const dateNow = new Date().toISOString();

    return (
      <>
        {!props.section && !article && (
          <Head>
            <title>Blog - Give InKind</title>
            <meta name="keywords" content="how to be supportive, support a friend, support a family member" />
            <meta
              name="description"
              content="Give InKind changes the way we give support during times of need. Read expert articles and guides from those who have been there before."
            />
            <meta property="og:type" content="website" />
            <meta property="og:updated_time" content={dateNow} />
            <meta
              property="og:title"
              content="Learn InKind: Read expert guides and personal stories about supporting loved ones through life's many different circumstances."
            />
            <meta
              property="og:description"
              content="Give InKind changes the way we give support during times of need. Create a care calendar, read expert articles and shop helpful gifts and services."
            />
          </Head>
        )}

        {props.section == BlogSections.generalResults && props.taxonomySlug === Taxonomy.ContentTag && (
          <Head>
            <title>{props.getGeneralResultsPageName()} - Give InKind</title>
          </Head>
        )}

        {article && (
          <Head>
            <meta property="og:type" content="article" />
            <meta property="og:article:author" content={article.authorFullName} />
            <meta property="og:article:published_time" content={article.publishedDate} />
            <meta property="og:article:updated_time" content={article.modifiedDate} />
            <meta property="og:article:section" content={article.taxonomyItemName} />
          </Head>
        )}

        {(situation || author || articleType) && (
          <Head>
            <meta property="og:type" content="website" />
            <meta property="og:updated_time" content={dateNow} />
          </Head>
        )}

        {article && renderYoastMetaTags(article.yoastMeta)}
        {author &&
          renderYoastMetaTags(
            author.yoastMeta,
            author.fullName,
            WordpressConverter.htmlToText(author.bio).substring(0, 160)
          )}
        {articleType && renderYoastMetaTags(articleType.yoastMeta, articleType.name)}
        {situation && renderYoastMetaTags(situation.yoastMeta, situation.name)}
      </>
    );
  }

  function renderYoastMetaTags(yoastMeta: WordpressYoastMeta, backupTitle = '', backupDescription = ''): JSX.Element {
    const removeShortcodes = (text: string) => text.replace(/\[.*?\]/g, '');

    return (
      <Head>
        <title>{yoastMeta.yoast_wpseo_title || backupTitle}</title>
        <meta name="description" content={removeShortcodes(yoastMeta.yoast_wpseo_metadesc || backupDescription)} />
        <meta name="keywords" content={yoastMeta.yoast_wpseo_focuskw} />
        <meta property="og:title" content={yoastMeta['yoast_wpseo_opengraph-title'] || backupTitle} />
        <meta
          property="og:description"
          content={removeShortcodes(yoastMeta['yoast_wpseo_opengraph-description'] || backupDescription)}
        />

        <meta property="og:image" content={yoastMeta['yoast_wpseo_opengraph-image']} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={yoastMeta['yoast_wpseo_twitter-title'] || backupTitle} />
        <meta
          name="twitter:description"
          content={removeShortcodes(yoastMeta['yoast_wpseo_twitter-description'] || backupDescription)}
        />
        <meta name="twitter:image" content={yoastMeta['yoast_wpseo_twitter-image']} />
      </Head>
    );
  }

  React.useEffect(() => {
    deleteDotNetLoadingSpinner();
  }, []);

  return (
    <article id="blog" className="blog">
      {props.renderHeadEnabled && _renderHead()}
      <Subnav
        situations={props.situations}
        categories={articleTypes}
        tags={props.tags}
        showReadingProgress={Boolean(props.article)}
        asPageHeading={!props.section && !props.article}
      />
      <main className="content">
        {!props.section && !props.article && <BlogHomePage articleLinkLists={props.articleLinkLists} />}
        {props.section === BlogSections.generalResults && props.pageSlug && (
          <GeneralResultsPage
            pageName={props.getGeneralResultsPageName()}
            articleLinks={props.articleLinkLists[0].articleLinks}
          />
        )}
        {props.article && (!props.article.isPreviewOnly || props.article.isPreviewOnly === getPreviewMode()) && (
          <ArticlePage article={props.article} articleLinks={props.articleLinkLists[0].articleLinks} />
        )}
        {props.section === BlogSections.authors && props.author && (
          <AuthorPage author={props.author} articleLinks={props.articleLinkLists[0].articleLinks} />
        )}
      </main>
      <ShareYourStory />
    </article>
  );
}
